import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Editor, Frame, Element, useEditor, useNode } from "@craftjs/core";
import { Layers } from "@craftjs/layers";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  ColorInput,
  SegmentedControl,
  Tabs,
  TextInput,
} from "@mantine/core";
import { ArrowLeft, Redo2, Undo2 } from "lucide-react";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../lib/usePerformRequest";

type ViewportMode = "desktop" | "tablet" | "mobile";
type CssSize = number | string;

type BuilderStyle = {
  width?: CssSize;
  minWidth?: CssSize;
  maxWidth?: CssSize;
  height?: CssSize;
  minHeight?: CssSize;
  maxHeight?: CssSize;

  marginTop?: CssSize;
  marginRight?: CssSize;
  marginBottom?: CssSize;
  marginLeft?: CssSize;

  paddingTop?: CssSize;
  paddingRight?: CssSize;
  paddingBottom?: CssSize;
  paddingLeft?: CssSize;

  display?: React.CSSProperties["display"];
  flexDirection?: React.CSSProperties["flexDirection"];
  justifyContent?: React.CSSProperties["justifyContent"];
  alignItems?: React.CSSProperties["alignItems"];
  gap?: CssSize;

  backgroundColor?: string;
  color?: string;

  fontSize?: CssSize;
  fontWeight?: React.CSSProperties["fontWeight"];
  lineHeight?: React.CSSProperties["lineHeight"];
  textAlign?: React.CSSProperties["textAlign"];

  borderStyle?: React.CSSProperties["borderStyle"];
  borderWidth?: CssSize;
  borderColor?: string;
  borderRadius?: CssSize;

  boxShadow?: string;

  position?: React.CSSProperties["position"];
  top?: CssSize;
  right?: CssSize;
  bottom?: CssSize;
  left?: CssSize;

  overflow?: React.CSSProperties["overflow"];
};

function toCssSize(v: any): any {
  if (v === "" || v === null || v === undefined) return undefined;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return undefined;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
    return trimmed;
  }
  return v;
}

function normalizeStyle(style?: BuilderStyle): React.CSSProperties {
  const s = style || {};
  return {
    width: toCssSize(s.width) as any,
    minWidth: toCssSize(s.minWidth) as any,
    maxWidth: toCssSize(s.maxWidth) as any,
    height: toCssSize(s.height) as any,
    minHeight: toCssSize(s.minHeight) as any,
    maxHeight: toCssSize(s.maxHeight) as any,

    marginTop: toCssSize(s.marginTop) as any,
    marginRight: toCssSize(s.marginRight) as any,
    marginBottom: toCssSize(s.marginBottom) as any,
    marginLeft: toCssSize(s.marginLeft) as any,

    paddingTop: toCssSize(s.paddingTop) as any,
    paddingRight: toCssSize(s.paddingRight) as any,
    paddingBottom: toCssSize(s.paddingBottom) as any,
    paddingLeft: toCssSize(s.paddingLeft) as any,

    display: s.display,
    flexDirection: s.flexDirection,
    justifyContent: s.justifyContent,
    alignItems: s.alignItems,
    gap: toCssSize(s.gap) as any,

    backgroundColor: s.backgroundColor,
    color: s.color,

    fontSize: toCssSize(s.fontSize) as any,
    fontWeight: s.fontWeight,
    lineHeight: s.lineHeight,
    textAlign: s.textAlign,

    borderStyle: s.borderStyle,
    borderWidth: toCssSize(s.borderWidth) as any,
    borderColor: s.borderColor,
    borderRadius: toCssSize(s.borderRadius) as any,

    boxShadow: s.boxShadow,

    position: s.position,
    top: toCssSize(s.top) as any,
    right: toCssSize(s.right) as any,
    bottom: toCssSize(s.bottom) as any,
    left: toCssSize(s.left) as any,

    overflow: s.overflow,
  };
}

const defaultSectionStyle: BuilderStyle = {
  width: "100%",
  paddingTop: 24,
  paddingBottom: 24,
  paddingLeft: 16,
  paddingRight: 16,
};

const defaultCardStyle: BuilderStyle = {
  backgroundColor: "#ffffff",
  borderStyle: "solid",
  borderWidth: 1,
  borderColor: "rgba(15, 23, 42, 0.08)",
  borderRadius: 12,
  paddingTop: 16,
  paddingBottom: 16,
  paddingLeft: 16,
  paddingRight: 16,
};

const defaultStackStyle: BuilderStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const defaultRowStyle: BuilderStyle = {
  display: "flex",
  flexDirection: "row",
  gap: 12,
  alignItems: "center",
};

export const Box: React.FC<{
  style?: BuilderStyle;
  children?: React.ReactNode;
}> = ({ style, children }) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <div
      ref={(ref) => ref && connect(drag(ref as any))}
      style={{
        ...normalizeStyle(style),
        outline: selected ? "2px solid #3b82f6" : "none",
      }}
    >
      {children}
    </div>
  );
};

(Box as any).craft = {
  displayName: "Box",
  props: {
    style: defaultSectionStyle,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
};

export const CanvasBox: React.FC<{
  style?: BuilderStyle;
  children?: React.ReactNode;
}> = ({ style, children }) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <div
      data-canvas-root
      ref={(ref) => ref && connect(drag(ref as any))}
      style={{
        position: "relative",
        ...normalizeStyle(style),
        outline: selected ? "2px solid #3b82f6" : "none",
        minHeight: 120,
      }}
    >
      {children}
    </div>
  );
};

(CanvasBox as any).craft = {
  displayName: "Canvas",
  props: {
    style: {
      ...defaultSectionStyle,
      backgroundColor: "#ffffff",
      borderStyle: "dashed",
      borderWidth: 2,
      borderColor: "rgba(15, 23, 42, 0.12)",
      borderRadius: 12,
      minHeight: 520,
    },
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
};

export const Stack: React.FC<{
  style?: BuilderStyle;
  children?: React.ReactNode;
}> = ({ style, children }) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <div
      ref={(ref) => ref && connect(drag(ref as any))}
      style={{
        ...normalizeStyle({ ...defaultStackStyle, ...(style || {}) }),
        outline: selected ? "2px solid #3b82f6" : "none",
      }}
    >
      {children}
    </div>
  );
};

(Stack as any).craft = {
  displayName: "Stack",
  props: {
    style: defaultStackStyle,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
};

export const Row: React.FC<{
  style?: BuilderStyle;
  children?: React.ReactNode;
}> = ({ style, children }) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <div
      ref={(ref) => ref && connect(drag(ref as any))}
      style={{
        ...normalizeStyle({ ...defaultRowStyle, ...(style || {}) }),
        outline: selected ? "2px solid #3b82f6" : "none",
        flexWrap: "wrap",
      }}
    >
      {children}
    </div>
  );
};

(Row as any).craft = {
  displayName: "Row",
  props: {
    style: defaultRowStyle,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
};

export const Card: React.FC<{
  style?: BuilderStyle;
  children?: React.ReactNode;
}> = ({ style, children }) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <div
      ref={(ref) => ref && connect(drag(ref as any))}
      style={{
        ...normalizeStyle({ ...defaultCardStyle, ...(style || {}) }),
        outline: selected ? "2px solid #3b82f6" : "none",
      }}
    >
      {children}
    </div>
  );
};

(Card as any).craft = {
  displayName: "Card",
  props: {
    style: defaultCardStyle,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
};

export const Heading: React.FC<{
  text?: string;
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  style?: BuilderStyle;
}> = ({ text = "Heading", level = 2, style }) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const Tag = `h${level}` as any as React.ElementType;

  return (
    <Tag
      ref={(ref: any) => ref && connect(drag(ref))}
      style={{
        margin: 0,
        ...normalizeStyle({
          color: "#0f172a",
          fontWeight: 800,
          ...(style || {}),
        }),
        outline: selected ? "1px dashed #3b82f6" : "none",
        padding: selected ? 4 : undefined,
        borderRadius: selected ? 6 : undefined,
      }}
      contentEditable={selected}
      suppressContentEditableWarning
      onBlur={(e) => {
        const v = e.currentTarget.textContent || "";
        setProp((p: any) => {
          p.text = v;
        });
      }}
    >
      {text}
    </Tag>
  );
};

(Heading as any).craft = {
  displayName: "Heading",
  props: {
    text: "Heading",
    level: 2,
    style: {
      fontSize: 34,
      lineHeight: 1.15,
    },
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};

export const Text: React.FC<{
  text?: string;
  style?: BuilderStyle;
}> = ({ text = "Double-click to edit text", style }) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  return (
    <p
      ref={(ref: any) => ref && connect(drag(ref))}
      style={{
        margin: 0,
        ...normalizeStyle({
          color: "#0f172a",
          fontSize: 16,
          lineHeight: 1.6,
          ...(style || {}),
        }),
        outline: selected ? "1px dashed #3b82f6" : "none",
        padding: selected ? 4 : undefined,
        borderRadius: selected ? 6 : undefined,
      }}
      contentEditable={selected}
      suppressContentEditableWarning
      onBlur={(e) => {
        const v = e.currentTarget.textContent || "";
        setProp((p: any) => {
          p.text = v;
        });
      }}
    >
      {text}
    </p>
  );
};

(Text as any).craft = {
  displayName: "Text",
  props: {
    text: "Double-click to edit text",
    style: {},
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};

export const ButtonBlock: React.FC<{
  label?: string;
  href?: string;
  style?: BuilderStyle;
}> = ({ label = "Button", href = "#", style }) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 14px",
    borderRadius: 10,
    backgroundColor: "#17265a",
    color: "#ffffff",
    fontWeight: 800,
    textDecoration: "none",
    userSelect: "none",
  };

  return (
    <a
      ref={(ref: any) => ref && connect(drag(ref))}
      href={href || "#"}
      onClick={(e) => {
        if (selected) e.preventDefault();
      }}
      style={{
        ...base,
        ...normalizeStyle(style),
        outline: selected ? "2px solid #3b82f6" : "none",
      }}
    >
      <span
        contentEditable={selected}
        suppressContentEditableWarning
        onBlur={(e) => {
          const v = e.currentTarget.textContent || "";
          setProp((p: any) => {
            p.label = v;
          });
        }}
      >
        {label}
      </span>
    </a>
  );
};

(ButtonBlock as any).craft = {
  displayName: "Button",
  props: {
    label: "Button",
    href: "#",
    style: {},
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};
export const Image: React.FC<{
  src?: string;
  alt?: string;
  style?: BuilderStyle;
}> = ({ src = "", alt = "Image", style }) => {
  const {
    connectors: { connect, drag },
    actions,
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const setStyleField = (key: keyof BuilderStyle, value: any) => {
    (actions as any).setProp?.((p: any) => {
      if (!p.style) p.style = {};
      (p.style as any)[key] = value;
    });
  };

  const getNumber = (v: any, fallback: number) => {
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    }
    return fallback;
  };

  const ensurePositionDefaults = () => {
    const position = (style as any)?.position;
    if (!position) setStyleField("position" as any, "absolute");
    if ((style as any)?.left == null) setStyleField("left" as any, 24);
    if ((style as any)?.top == null) setStyleField("top" as any, 24);
    if ((style as any)?.width == null) setStyleField("width" as any, 320);
    if ((style as any)?.height == null) setStyleField("height" as any, 220);
  };

  if (!src) {
    return (
      <div
        ref={(ref: any) => ref && connect(drag(ref))}
        style={{
          width: "100%",
          minHeight: 180,
          borderRadius: 14,
          border: "2px dashed rgba(15, 23, 42, 0.18)",
          background: "rgba(248, 250, 252, 0.9)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 14,
          textAlign: "center",
          color: "#64748b",
          fontSize: 12,
          fontWeight: 700,
          ...normalizeStyle(style),
          outline: selected ? "2px solid #3b82f6" : "none",
        }}
      >
        Select this block, then use the Upload control in the inspector.
      </div>
    );
  }

  const left = getNumber((style as any)?.left, 24);
  const top = getNumber((style as any)?.top, 24);
  const width = getNumber((style as any)?.width, 320);
  const height = getNumber((style as any)?.height, 220);

  const clamp = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));

  const getCanvasEl = (el: HTMLElement | null) => {
    const canvas = el?.closest?.("[data-canvas-root]") as HTMLElement | null;
    return canvas || (el?.parentElement as HTMLElement | null);
  };

  const startDrag = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    ensurePositionDefaults();
    const el = e.currentTarget as HTMLElement | null;
    const canvas = getCanvasEl(el);
    const parentW = canvas?.clientWidth ?? 0;
    const parentH = canvas?.clientHeight ?? 0;
    const maxLeft =
      parentW > 0 ? Math.max(0, parentW - width) : Number.POSITIVE_INFINITY;
    const maxTop =
      parentH > 0 ? Math.max(0, parentH - height) : Number.POSITIVE_INFINITY;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = left;
    const startTop = top;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const nextLeft = clamp(Math.round(startLeft + dx), 0, maxLeft);
      const nextTop = clamp(Math.round(startTop + dy), 0, maxTop);
      setStyleField("left" as any, nextLeft);
      setStyleField("top" as any, nextTop);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  type HandleDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

  const startResize = (dir: HandleDir) => (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    ensurePositionDefaults();

    const wrapper = (e.currentTarget as HTMLElement | null)
      ?.parentElement as HTMLElement | null;
    const canvas = getCanvasEl(wrapper);
    const parentW = canvas?.clientWidth ?? 0;
    const parentH = canvas?.clientHeight ?? 0;

    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = left;
    const startTop = top;
    const startW = Math.max(10, width);
    const startH = Math.max(10, height);
    const minW = 40;
    const minH = 40;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      let nextLeft = startLeft;
      let nextTop = startTop;
      let nextW = startW;
      let nextH = startH;

      if (dir.includes("e")) nextW = Math.max(minW, startW + dx);
      if (dir.includes("s")) nextH = Math.max(minH, startH + dy);

      if (dir.includes("w")) {
        nextW = Math.max(minW, startW - dx);
        nextLeft = startLeft + (startW - nextW);
      }
      if (dir.includes("n")) {
        nextH = Math.max(minH, startH - dy);
        nextTop = startTop + (startH - nextH);
      }

      // Clamp within parent bounds when we can measure them
      if (parentW > 0) {
        nextLeft = clamp(nextLeft, 0, Math.max(0, parentW - nextW));
        nextW = clamp(nextW, minW, Math.max(minW, parentW - nextLeft));
      }
      if (parentH > 0) {
        nextTop = clamp(nextTop, 0, Math.max(0, parentH - nextH));
        nextH = clamp(nextH, minH, Math.max(minH, parentH - nextTop));
      }

      setStyleField("left" as any, Math.round(nextLeft));
      setStyleField("top" as any, Math.round(nextTop));
      setStyleField("width" as any, Math.round(nextW));
      setStyleField("height" as any, Math.round(nextH));
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleBase: React.CSSProperties = {
    position: "absolute",
    width: 10,
    height: 10,
    background: "#ffffff",
    border: "1px solid rgba(15, 23, 42, 0.35)",
    borderRadius: 999,
    zIndex: 3,
  };

  return (
    <div
      ref={(ref: any) => ref && connect(drag(ref))}
      onMouseDown={startDrag}
      style={{
        position: (style as any)?.position || ("absolute" as any),
        left,
        top,
        width,
        height,
        cursor: selected ? "move" : "pointer",
        userSelect: "none",
        ...normalizeStyle(style),
        outline: selected ? "2px solid #3b82f6" : "none",
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 14,
          display: "block",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />

      {selected && (
        <>
          <div
            style={{ ...handleBase, left: -5, top: -5, cursor: "nwse-resize" }}
            onMouseDown={startResize("nw")}
          />
          <div
            style={{
              ...handleBase,
              left: "50%",
              top: -5,
              transform: "translateX(-50%)",
              cursor: "ns-resize",
            }}
            onMouseDown={startResize("n")}
          />
          <div
            style={{ ...handleBase, right: -5, top: -5, cursor: "nesw-resize" }}
            onMouseDown={startResize("ne")}
          />

          <div
            style={{
              ...handleBase,
              right: -5,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "ew-resize",
            }}
            onMouseDown={startResize("e")}
          />
          <div
            style={{
              ...handleBase,
              right: -5,
              bottom: -5,
              cursor: "nwse-resize",
            }}
            onMouseDown={startResize("se")}
          />
          <div
            style={{
              ...handleBase,
              left: "50%",
              bottom: -5,
              transform: "translateX(-50%)",
              cursor: "ns-resize",
            }}
            onMouseDown={startResize("s")}
          />
          <div
            style={{
              ...handleBase,
              left: -5,
              bottom: -5,
              cursor: "nesw-resize",
            }}
            onMouseDown={startResize("sw")}
          />
          <div
            style={{
              ...handleBase,
              left: -5,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "ew-resize",
            }}
            onMouseDown={startResize("w")}
          />
        </>
      )}
    </div>
  );
};

(Image as any).craft = {
  displayName: "Image",
  props: {
    src: "",
    alt: "Image",
    style: {},
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};

export const NavbarSection: React.FC<{
  brand?: string;
  logoSrc?: string;
  links?: Array<{ label: string; href: string }>;
  linksText?: string;
  sticky?: boolean;
  showCta?: boolean;
  ctaLabel?: string;
  ctaHref?: string;
  style?: BuilderStyle;
}> = ({
  brand = "Brand",
  logoSrc = "",
  links,
  linksText = "Home|#\nAbout|#about\nContact|#contact",
  sticky = false,
  showCta = false,
  ctaLabel = "Get started",
  ctaHref = "#",
  style,
}) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  const [open, setOpen] = useState(false);
  const uidRef = useRef<string>(
    `nb_${Math.random().toString(36).slice(2, 9)}_${Date.now()}`
  );
  const uid = uidRef.current;

  const computedLinks = useMemo(() => {
    if (Array.isArray(links) && links.length > 0) return links;
    const raw = String(linksText || "");
    return raw
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const [label, href] = line.split("|");
        return {
          label: (label || "Link").trim(),
          href: (href || "#").trim(),
        };
      });
  }, [links, linksText]);

  return (
    <header
      ref={(ref: any) => ref && connect(drag(ref))}
      style={{
        position: sticky ? ("sticky" as any) : ("relative" as any),
        top: sticky ? 0 : undefined,
        zIndex: 50,
        width: "100%",
        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        background: "#ffffff",
        ...normalizeStyle(style),
        outline: selected ? "2px solid #3b82f6" : "none",
      }}
    >
      <style>{`
        .${uid} { max-width: 1280px; margin: 0 auto; padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .${uid}__left { display: flex; align-items: center; gap: 10px; min-width: 0; }
        .${uid}__brand { font-weight: 800; color: inherit; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .${uid}__nav { display: flex; align-items: center; gap: 14px; }
        .${uid}__link { color: inherit; text-decoration: none; font-weight: 600; font-size: 14px; opacity: 0.9; }
        .${uid}__link:hover { opacity: 1; text-decoration: underline; }
        .${uid}__burger { display: none; border: 1px solid rgba(15, 23, 42, 0.12); background: transparent; color: inherit; border-radius: 10px; padding: 8px 10px; font-weight: 800; }
        .${uid}__mobile { display: none; border-top: 1px solid rgba(15, 23, 42, 0.08); padding: 10px 16px; }
        .${uid}__mobile a { display: block; padding: 10px 0; color: inherit; text-decoration: none; font-weight: 700; }
        .${uid}__cta { background: #0f172a; color: #ffffff; border-radius: 12px; padding: 10px 12px; font-weight: 800; text-decoration: none; display: inline-flex; align-items: center; justify-content: center; }
        @media (max-width: 768px) {
          .${uid}__nav { display: none; }
          .${uid}__burger { display: inline-flex; align-items: center; gap: 8px; }
          .${uid}__mobile { display: ${open ? "block" : "none"}; }
        }
      `}</style>

      <div className={uid}>
        <div className={`${uid}__left`}>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt="Logo"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                objectFit: "cover",
              }}
            />
          ) : null}
          <div className={`${uid}__brand`}>{brand}</div>
        </div>

        <nav className={`${uid}__nav`}>
          {computedLinks.map((l, idx) => (
            <a
              key={`${l.href}-${idx}`}
              href={l.href}
              className={`${uid}__link`}
            >
              {l.label}
            </a>
          ))}
          {showCta ? (
            <a href={ctaHref} className={`${uid}__cta`}>
              {ctaLabel}
            </a>
          ) : null}
        </nav>

        <button
          type="button"
          className={`${uid}__burger`}
          onClick={() => setOpen((v) => !v)}
        >
          Menu
        </button>
      </div>

      <div className={`${uid}__mobile`}>
        {computedLinks.map((l, idx) => (
          <a
            key={`${l.href}-${idx}-m`}
            href={l.href}
            onClick={() => setOpen(false)}
          >
            {l.label}
          </a>
        ))}
        {showCta ? (
          <a
            href={ctaHref}
            onClick={() => setOpen(false)}
            className={`${uid}__cta`}
          >
            {ctaLabel}
          </a>
        ) : null}
      </div>
    </header>
  );
};

(NavbarSection as any).craft = {
  displayName: "Navbar",
  props: {
    brand: "Brand",
    logoSrc: "",
    links: [
      { label: "Home", href: "#" },
      { label: "About", href: "#about" },
      { label: "Contact", href: "#contact" },
    ],
    sticky: false,
    showCta: false,
    ctaLabel: "Get started",
    ctaHref: "#",
    style: {
      backgroundColor: "#ffffff",
      color: "#0f172a",
    },
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => false,
  },
};

const ResizableViewport: React.FC<{
  mode: ViewportMode;
  children: React.ReactNode;
}> = ({ mode, children }) => {
  const width =
    mode === "mobile" ? 390 : mode === "tablet" ? 820 : "min(1280px, 100%)";

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          backgroundColor: "#f8fafc",
          border: "1px solid rgba(15, 23, 42, 0.08)",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const Toolbox: React.FC = () => {
  const { connectors } = useEditor();

  const groupStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 8,
  };

  const itemStyle: React.CSSProperties = {
    padding: "10px 10px",
    borderRadius: 12,
    border: "1px solid rgba(15, 23, 42, 0.10)",
    background: "#ffffff",
    cursor: "grab",
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
    userSelect: "none",
  };

  const make = (node: React.ReactElement) => (ref: HTMLElement | null) => {
    if (!ref) return;
    connectors.create(ref as any, node);
  };

  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
        Blocks
      </div>
      <div style={{ height: 8 }} />
      <div style={groupStyle}>
        <div ref={make(<NavbarSection />)} style={itemStyle}>
          Navbar
        </div>
        <div
          ref={make(<Element is={Box} canvas style={defaultSectionStyle} />)}
          style={itemStyle}
        >
          Section
        </div>
        <div ref={make(<Element is={Card} canvas />)} style={itemStyle}>
          Card
        </div>
        <div ref={make(<Element is={Stack} canvas />)} style={itemStyle}>
          Stack
        </div>
        <div ref={make(<Element is={Row} canvas />)} style={itemStyle}>
          Row
        </div>
        <div ref={make(<Heading />)} style={itemStyle}>
          Heading
        </div>
        <div ref={make(<Text />)} style={itemStyle}>
          Text
        </div>
        <div ref={make(<ButtonBlock />)} style={itemStyle}>
          Button
        </div>
        <div ref={make(<Image />)} style={itemStyle}>
          Image
        </div>
      </div>
      <div style={{ height: 10 }} />
      <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
        Drag blocks onto the canvas. Select a block to edit styles.
      </div>
    </div>
  );
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div style={{ marginBottom: 10 }}>
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#0f172a",
        marginBottom: 6,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const StyleInspector: React.FC<{ nodeId: string }> = ({ nodeId }) => {
  const { actions, displayName, props } = useEditor((state) => {
    const node = (state.nodes as any)?.[nodeId];
    return {
      displayName: node?.data?.displayName || "Selected",
      props: (node?.data?.props || {}) as any,
    };
  });
  const style = ((props as any).style || {}) as BuilderStyle;

  const shadowColor = useMemo(() => {
    const s = String(style.boxShadow ?? "").trim();
    if (!s) return "";
    const tokens = s.split(/\s+/);
    const last = tokens[tokens.length - 1] || "";
    if (/^#/.test(last) || /^rgba?\(/i.test(last) || /^hsla?\(/i.test(last))
      return last;
    return "";
  }, [style.boxShadow]);

  const setShadowColor = (newColor: string) => {
    const s = String(style.boxShadow ?? "").trim();
    if (!s) {
      setStyleField("boxShadow", `0 10px 30px ${newColor}`);
      return;
    }
    const tokens = s.split(/\s+/);
    const last = tokens[tokens.length - 1] || "";
    const hasColor =
      /^#/.test(last) || /^rgba?\(/i.test(last) || /^hsla?\(/i.test(last);
    if (hasColor) {
      tokens[tokens.length - 1] = newColor;
      setStyleField("boxShadow", tokens.join(" "));
      return;
    }
    setStyleField("boxShadow", `${s} ${newColor}`);
  };

  const setStyleField = (key: keyof BuilderStyle, value: any) => {
    actions.setProp(nodeId, (p: any) => {
      if (!p.style) p.style = {};
      (p.style as any)[key] = value;
    });
  };

  const setField = (key: string, value: any) => {
    actions.setProp(nodeId, (p: any) => {
      (p as any)[key] = value;
    });
  };

  const uploadImage = async (file: File) => {
    const { route, method } = Endpoints.GetUploadFile();

    const findCandidateUrl = (value: any, depth = 0): string | null => {
      if (depth > 6) return null;
      if (typeof value === "string") {
        const s = value.trim();
        if (!s) return null;
        if (/^https?:\/\//i.test(s)) return s;
        if (s.startsWith("/")) return s;
        if (/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(s)) return s;
        return null;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          const found = findCandidateUrl(item, depth + 1);
          if (found) return found;
        }
        return null;
      }
      if (value && typeof value === "object") {
        const preferredKeys = [
          "url",
          "secure_url",
          "image",
          "imageUrl",
          "file",
          "fileUrl",
          "location",
          "path",
          "src",
        ];
        for (const k of preferredKeys) {
          if (k in value) {
            const found = findCandidateUrl((value as any)[k], depth + 1);
            if (found) return found;
          }
        }
        for (const k of Object.keys(value)) {
          const found = findCandidateUrl((value as any)[k], depth + 1);
          if (found) return found;
        }
      }
      return null;
    };

    const normalizeUrl = (candidateRaw: string) => {
      try {
        const origin = new URL(route).origin;
        return /^https?:\/\//i.test(candidateRaw)
          ? candidateRaw
          : candidateRaw.startsWith("/")
          ? `${origin}${candidateRaw}`
          : `${origin}/${candidateRaw}`;
      } catch {
        return candidateRaw;
      }
    };

    const form = new FormData();
    form.append("file", file);
    form.append("image", file);
    form.append("files", file);

    const r = await PerformRequest<any>({
      method: method as any,
      route,
      data: form,
      headers: { "Content-Type": "multipart/form-data" },
    });

    const axiosResponseLike = (r as any)?.data;
    const body = axiosResponseLike?.data ?? axiosResponseLike;
    const candidateRaw =
      findCandidateUrl(body) || findCandidateUrl(axiosResponseLike);

    if (candidateRaw) {
      setField("src", normalizeUrl(candidateRaw));
    } else {
      console.warn("Image upload succeeded but no URL found in response", body);
    }
  };

  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
        {displayName}
      </div>
      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
        Node: {nodeId}
      </div>

      <div style={{ height: 10 }} />

      <Tabs defaultValue="design" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="design">Design</Tabs.Tab>
          <Tabs.Tab value="layout">Layout</Tabs.Tab>
          <Tabs.Tab value="content">Content</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="design" pt="sm">
          <FieldRow label="Background">
            <ColorInput
              value={style.backgroundColor || ""}
              placeholder="#ffffff"
              onChange={(v) => setStyleField("backgroundColor", v)}
              format="hex"
              withEyeDropper
              swatchesPerRow={8}
            />
          </FieldRow>
          <FieldRow label="Text Color">
            <ColorInput
              value={style.color || ""}
              placeholder="#0f172a"
              onChange={(v) => setStyleField("color", v)}
              format="hex"
              withEyeDropper
              swatchesPerRow={8}
            />
          </FieldRow>
          <FieldRow label="Border Radius">
            <TextInput
              value={String(style.borderRadius ?? "")}
              placeholder="12"
              onChange={(e) => setStyleField("borderRadius", e.target.value)}
            />
          </FieldRow>
          <FieldRow label="Border Width">
            <TextInput
              value={String(style.borderWidth ?? "")}
              placeholder="1"
              onChange={(e) => setStyleField("borderWidth", e.target.value)}
            />
          </FieldRow>
          <FieldRow label="Border Color">
            <ColorInput
              value={style.borderColor || ""}
              placeholder="rgba(0,0,0,0.1)"
              onChange={(v) => setStyleField("borderColor", v)}
              format="hex"
              withEyeDropper
              swatchesPerRow={8}
            />
          </FieldRow>
          <FieldRow label="Box Shadow">
            <TextInput
              value={style.boxShadow || ""}
              placeholder="0 10px 30px rgba(0,0,0,0.1)"
              onChange={(e) => setStyleField("boxShadow", e.target.value)}
            />
          </FieldRow>
          <FieldRow label="Shadow Color">
            <ColorInput
              value={shadowColor}
              placeholder="rgba(0,0,0,0.1)"
              onChange={(v) => setShadowColor(v)}
              format="hex"
              withEyeDropper
              swatchesPerRow={8}
            />
          </FieldRow>
        </Tabs.Panel>

        <Tabs.Panel value="layout" pt="sm">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
            }}
          >
            <FieldRow label="Padding Top">
              <TextInput
                value={String(style.paddingTop ?? "")}
                placeholder="24"
                onChange={(e) => setStyleField("paddingTop", e.target.value)}
              />
            </FieldRow>
            <FieldRow label="Padding Bottom">
              <TextInput
                value={String(style.paddingBottom ?? "")}
                placeholder="24"
                onChange={(e) => setStyleField("paddingBottom", e.target.value)}
              />
            </FieldRow>
            <FieldRow label="Padding Left">
              <TextInput
                value={String(style.paddingLeft ?? "")}
                placeholder="16"
                onChange={(e) => setStyleField("paddingLeft", e.target.value)}
              />
            </FieldRow>
            <FieldRow label="Padding Right">
              <TextInput
                value={String(style.paddingRight ?? "")}
                placeholder="16"
                onChange={(e) => setStyleField("paddingRight", e.target.value)}
              />
            </FieldRow>
          </div>

          <FieldRow label="Width">
            <TextInput
              value={String(style.width ?? "")}
              placeholder="100%"
              onChange={(e) => setStyleField("width", e.target.value)}
            />
          </FieldRow>
          <FieldRow label="Max Width">
            <TextInput
              value={String(style.maxWidth ?? "")}
              placeholder="1280"
              onChange={(e) => setStyleField("maxWidth", e.target.value)}
            />
          </FieldRow>

          <FieldRow label="Display">
            <SegmentedControl
              size="xs"
              value={(style.display as any) || "block"}
              onChange={(v) => setStyleField("display", v as any)}
              data={[
                { label: "block", value: "block" },
                { label: "flex", value: "flex" },
                { label: "grid", value: "grid" },
              ]}
            />
          </FieldRow>

          {style.display === "flex" && (
            <>
              <FieldRow label="Flex Direction">
                <SegmentedControl
                  size="xs"
                  value={(style.flexDirection as any) || "row"}
                  onChange={(v) => setStyleField("flexDirection", v as any)}
                  data={[
                    { label: "row", value: "row" },
                    { label: "column", value: "column" },
                  ]}
                />
              </FieldRow>
              <FieldRow label="Gap">
                <TextInput
                  value={String(style.gap ?? "")}
                  placeholder="12"
                  onChange={(e) => setStyleField("gap", e.target.value)}
                />
              </FieldRow>
            </>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="content" pt="sm">
          {displayName === "Button" && (
            <FieldRow label="Href">
              <TextInput
                value={String(props.href ?? "")}
                placeholder="#"
                onChange={(e) => setField("href", e.target.value)}
              />
            </FieldRow>
          )}

          {displayName === "Navbar" && (
            <>
              <FieldRow label="Brand">
                <TextInput
                  value={String((props as any).brand ?? "")}
                  onChange={(e) => setField("brand", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Logo URL">
                <TextInput
                  value={String((props as any).logoSrc ?? "")}
                  placeholder="https://..."
                  onChange={(e) => setField("logoSrc", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Nav Items">
                <div style={{ display: "grid", gap: 10 }}>
                  {(Array.isArray((props as any).links)
                    ? ((props as any).links as any[])
                    : []
                  ).map((item, idx) => (
                    <div
                      key={`${idx}`}
                      style={{
                        border: "1px solid rgba(15, 23, 42, 0.10)",
                        borderRadius: 12,
                        padding: 10,
                        background: "#ffffff",
                        display: "grid",
                        gap: 8,
                      }}
                    >
                      <TextInput
                        value={String((item as any)?.label ?? "")}
                        placeholder="Label"
                        onChange={(e) => {
                          const next = [
                            ...(((props as any).links || []) as any[]),
                          ];
                          next[idx] = {
                            ...(next[idx] || {}),
                            label: e.target.value,
                          };
                          setField("links", next);
                        }}
                      />
                      <TextInput
                        value={String((item as any)?.href ?? "")}
                        placeholder="Href (e.g. #about or /page)"
                        onChange={(e) => {
                          const next = [
                            ...(((props as any).links || []) as any[]),
                          ];
                          next[idx] = {
                            ...(next[idx] || {}),
                            href: e.target.value,
                          };
                          setField("links", next);
                        }}
                      />
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          size="xs"
                          variant="light"
                          color="red"
                          onClick={() => {
                            const next = [
                              ...(((props as any).links || []) as any[]),
                            ].filter((_, i) => i !== idx);
                            setField("links", next);
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}

                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => {
                      const next = [
                        ...(((props as any).links || []) as any[]),
                        { label: "New", href: "#" },
                      ];
                      setField("links", next);
                    }}
                  >
                    Add item
                  </Button>
                </div>
              </FieldRow>
              <FieldRow label="Sticky (top)">
                <SegmentedControl
                  size="xs"
                  value={String(Boolean((props as any).sticky))}
                  onChange={(v) => setField("sticky", v === "true")}
                  data={[
                    { label: "off", value: "false" },
                    { label: "on", value: "true" },
                  ]}
                />
              </FieldRow>
              <FieldRow label="CTA">
                <SegmentedControl
                  size="xs"
                  value={String(Boolean((props as any).showCta))}
                  onChange={(v) => setField("showCta", v === "true")}
                  data={[
                    { label: "off", value: "false" },
                    { label: "on", value: "true" },
                  ]}
                />
              </FieldRow>
              {Boolean((props as any).showCta) && (
                <>
                  <FieldRow label="CTA Label">
                    <TextInput
                      value={String((props as any).ctaLabel ?? "")}
                      onChange={(e) => setField("ctaLabel", e.target.value)}
                    />
                  </FieldRow>
                  <FieldRow label="CTA Href">
                    <TextInput
                      value={String((props as any).ctaHref ?? "")}
                      onChange={(e) => setField("ctaHref", e.target.value)}
                    />
                  </FieldRow>
                </>
              )}
            </>
          )}

          {displayName === "Image" && (
            <>
              <FieldRow label="Src">
                <TextInput
                  value={String(props.src ?? "")}
                  onChange={(e) => setField("src", e.target.value)}
                />
              </FieldRow>
              <FieldRow label="Upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const f = (e.target as any)?.files?.[0];
                    if (f) await uploadImage(f);
                    (e.target as any).value = "";
                  }}
                />
              </FieldRow>
              <FieldRow label="Alt">
                <TextInput
                  value={String(props.alt ?? "")}
                  onChange={(e) => setField("alt", e.target.value)}
                />
              </FieldRow>
            </>
          )}

          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
            For text content, select the block then double-click to edit.
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

const RightPanel: React.FC = () => {
  const { selectedNodeId, nodeName } = useEditor((state, query) => {
    const selectedSet = state.events.selected as any;
    const id =
      selectedSet && selectedSet.size
        ? (Array.from(selectedSet)[0] as string)
        : null;
    const name = id ? query.node(id).get().data.displayName : "";
    return { selectedNodeId: id, nodeName: name };
  });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div
        style={{
          borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
          background: "#ffffff",
        }}
      >
        <Tabs defaultValue="inspect" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="inspect">Inspector</Tabs.Tab>
            <Tabs.Tab value="layers">Layers</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="inspect" pt="xs">
            {selectedNodeId ? (
              <StyleInspector nodeId={selectedNodeId} />
            ) : (
              <div style={{ padding: 12, color: "#64748b", fontSize: 12 }}>
                Select a block to edit.
              </div>
            )}
          </Tabs.Panel>
          <Tabs.Panel value="layers" pt="xs">
            <div style={{ padding: 12 }}>
              <Layers />
            </div>
          </Tabs.Panel>
        </Tabs>
      </div>

      <div style={{ flex: 1 }} />

      <div
        style={{
          padding: 10,
          borderTop: "1px solid rgba(15, 23, 42, 0.08)",
          fontSize: 12,
          color: "#64748b",
          background: "#ffffff",
        }}
      >
        {selectedNodeId
          ? `Selected: ${nodeName || selectedNodeId}`
          : "No selection"}
      </div>
    </div>
  );
};

const BuilderTopbar: React.FC<{
  onBack: () => void;
  viewport: ViewportMode;
  setViewport: (v: ViewportMode) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onSaveDraft: () => void;
  onLoadDraft: () => void;
  onPublish: () => void;
  onLoadPublished: () => void;
  onReset: () => void;
  onExport: () => void;
  onImport: () => void;
}> = ({
  onBack,
  viewport,
  setViewport,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSaveDraft,
  onLoadDraft,
  onPublish,
  onLoadPublished,
  onReset,
  onExport,
  onImport,
}) => {
  return (
    <div
      style={{
        height: 56,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 12px",
        borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
        backgroundColor: "#ffffff",
        position: "sticky",
        top: 0,
        zIndex: 50,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Button
          variant="subtle"
          leftSection={<ArrowLeft size={18} />}
          onClick={onBack}
          size="sm"
        >
          Back
        </Button>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
          Organization Page Builder
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <SegmentedControl
          size="xs"
          value={viewport}
          onChange={(v) => setViewport(v as ViewportMode)}
          data={[
            { label: "Desktop", value: "desktop" },
            { label: "Tablet", value: "tablet" },
            { label: "Mobile", value: "mobile" },
          ]}
        />

        <Button
          variant="light"
          size="xs"
          onClick={onUndo}
          disabled={!canUndo}
          leftSection={<Undo2 size={14} />}
        >
          Undo
        </Button>
        <Button
          variant="light"
          size="xs"
          onClick={onRedo}
          disabled={!canRedo}
          leftSection={<Redo2 size={14} />}
        >
          Redo
        </Button>

        <Button variant="default" size="xs" onClick={onSaveDraft}>
          Save Draft
        </Button>
        <Button variant="default" size="xs" onClick={onLoadDraft}>
          Load Draft
        </Button>
        <Button variant="filled" size="xs" onClick={onPublish}>
          Publish
        </Button>
        <Button variant="default" size="xs" onClick={onLoadPublished}>
          Load Published
        </Button>

        <Button variant="light" size="xs" onClick={onExport}>
          Export
        </Button>
        <Button variant="light" size="xs" onClick={onImport}>
          Import
        </Button>
        <Button color="red" variant="light" size="xs" onClick={onReset}>
          Reset
        </Button>
      </div>
    </div>
  );
};

const BuilderBody: React.FC<{ orgId: string }> = ({ orgId }) => {
  const navigate = useNavigate();
  const draftKey = `craft_v2_draft_org_${orgId}`;
  const publishedKey = `craft_v2_published_org_${orgId}`;

  const [viewport, setViewport] = useState<ViewportMode>("desktop");
  const [importValue, setImportValue] = useState<string>("");
  const initialSerializedRef = useRef<string | null>(null);

  const { actions, query, canUndo, canRedo } = useEditor((state) => {
    const history = (state as any).history;
    return {
      canUndo: Boolean(history?.past?.length),
      canRedo: Boolean(history?.future?.length),
    };
  });

  const serialize = useCallback(() => query.serialize(), [query]);
  const deserialize = useCallback(
    (json: string) => actions.deserialize(json),
    [actions]
  );

  const saveDraft = () => localStorage.setItem(draftKey, serialize());
  const loadDraft = () => {
    const json = localStorage.getItem(draftKey);
    if (json) deserialize(json);
  };

  const publish = () => localStorage.setItem(publishedKey, serialize());
  const loadPublished = () => {
    const json = localStorage.getItem(publishedKey);
    if (json) deserialize(json);
  };

  const reset = () => {
    if (initialSerializedRef.current) deserialize(initialSerializedRef.current);
  };

  const exportJson = () => setImportValue(serialize());
  const importJson = () => {
    if (importValue.trim().length > 0) deserialize(importValue);
  };

  const undo = () => (actions as any).history?.undo?.();
  const redo = () => (actions as any).history?.redo?.();

  useEffect(() => {
    if (!initialSerializedRef.current)
      initialSerializedRef.current = serialize();
    const json = localStorage.getItem(draftKey);
    if (json) {
      try {
        deserialize(json);
      } catch {
        // ignore
      }
    }
  }, [deserialize, draftKey, serialize]);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <BuilderTopbar
        onBack={() => navigate(`/d/organizations/${orgId}`)}
        viewport={viewport}
        setViewport={setViewport}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSaveDraft={saveDraft}
        onLoadDraft={loadDraft}
        onPublish={publish}
        onLoadPublished={loadPublished}
        onReset={reset}
        onExport={exportJson}
        onImport={importJson}
      />

      <div
        style={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: "320px 1fr 360px",
        }}
      >
        <div
          style={{
            borderRight: "1px solid rgba(15, 23, 42, 0.08)",
            background: "#f8fafc",
            overflow: "auto",
          }}
        >
          <Toolbox />
          <div style={{ padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>
              Import/Export
            </div>
            <div style={{ height: 8 }} />
            <textarea
              value={importValue}
              onChange={(e) => setImportValue(e.target.value)}
              style={{
                width: "100%",
                height: 140,
                resize: "vertical",
                borderRadius: 12,
                border: "1px solid rgba(15, 23, 42, 0.12)",
                padding: 10,
                fontSize: 11,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              }}
            />
          </div>
        </div>

        <div style={{ overflow: "auto", background: "#f1f5f9" }}>
          <ResizableViewport mode={viewport}>
            <div style={{ background: "#ffffff" }}>
              <Frame>
                <Element is={CanvasBox} canvas />
              </Frame>
            </div>
          </ResizableViewport>
        </div>

        <div
          style={{
            borderLeft: "1px solid rgba(15, 23, 42, 0.08)",
            background: "#f8fafc",
            overflow: "auto",
          }}
        >
          <RightPanel />
        </div>
      </div>
    </div>
  );
};

const CraftPageBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const orgId = id || "";

  const resolver = useMemo(
    () => ({
      Box,
      CanvasBox,
      Stack,
      Row,
      Card,
      Heading,
      Text,
      ButtonBlock,
      Image,
      NavbarSection,
    }),
    []
  );

  if (!orgId) {
    return (
      <div style={{ padding: 24, color: "#0f172a" }}>
        Missing organization id
      </div>
    );
  }

  return (
    <Editor resolver={resolver} enabled>
      <BuilderBody orgId={orgId} />
    </Editor>
  );
};

export default CraftPageBuilder;
